/**
 * Tratamento compartilhado para tooltips ECharts (trigger=axis) com muitas
 * séries.
 *
 * Precedente: Windx (`components/Windx/ChartComSliders/tooltipFormatter.ts`,
 * bug PO 2026-05-27) — com dezenas de séries o tooltip default ultrapassa o
 * viewport e ainda lista séries sem valor no ponto (renderizadas como N/A).
 *
 * Solução A+B, como no Windx:
 * - A: FILTRAR séries com valor nulo/inválido no X hovered (0 É válido);
 * - B: SCROLL defensivo — `confine` + `appendToBody` mantêm o tooltip no
 *   viewport; `enterable` + `pointer-events: auto` permitem entrar com o
 *   mouse e rolar; `extraCssText` limita a 60vh.
 */

const SCROLLABLE_TOOLTIP_PROPS = {
  confine: true,
  appendToBody: true,
  enterable: true,
  extraCssText: 'max-height: 60vh; overflow-y: auto; pointer-events: auto;',
};

/** Shape mínimo dos params do ECharts no trigger=axis — só o que usamos. */
export interface AxisTooltipParam {
  axisValue?: string | number,
  axisValueLabel?: string,
  marker?: string,
  seriesName?: string,
  value?: unknown,
}

/** Valor plotado da série: em eixo time o value é [x, y] — pega o y. */
const extractY = (value: unknown): unknown => (
  Array.isArray(value) ? value[value.length - 1] : value
);

/** 0 é válido; null/undefined/NaN/''/'—'/'-' não aparecem no tooltip. */
const isValidY = (y: unknown): boolean => {
  if (y == null) return false;
  if (typeof y === 'string' && (y.trim() === '' || y === '—' || y === '-')) return false;
  return Number.isFinite(typeof y === 'number' ? y : Number(y));
};

/** Header do tooltip: data pt-BR (eixo time entrega timestamp em ms). */
const headerLabel = (first: AxisTooltipParam): string => {
  if (typeof first.axisValue === 'number') {
    return new Date(first.axisValue).toLocaleDateString('pt-BR');
  }
  return first.axisValueLabel ?? String(first.axisValue ?? '');
};

/**
 * Monta o objeto `tooltip` completo (scroll + filtro de N/A + ordenação por
 * valor decrescente), preservando a formatação de valor de cada gráfico via
 * `formatValue`.
 */
export default function buildScrollableAxisTooltip(formatValue: (v: number) => string) {
  return {
    ...SCROLLABLE_TOOLTIP_PROPS,
    trigger: 'axis',
    order: 'valueDesc',
    formatter: (params: AxisTooltipParam[]): string => {
      if (!Array.isArray(params) || params.length === 0) return '';
      const validos = params.filter((p) => isValidY(extractY(p.value)));
      if (validos.length === 0) return '';
      const porValorDesc = [...validos].sort(
        (a, b) => Number(extractY(b.value)) - Number(extractY(a.value)),
      );
      const linhas = porValorDesc.map((p) => (
        `${p.marker ?? ''} ${p.seriesName ?? ''}: <b>${formatValue(Number(extractY(p.value)))}</b>`
      ));
      return `<b>${headerLabel(params[0])}</b><br/>${linhas.join('<br/>')}`;
    },
    axisPointer: {
      label: {
        formatter: (params: { value: string | number }) => (
          new Date(params.value).toLocaleDateString('pt-BR')
        ),
      },
    },
  };
}
